class AutoplayInlineVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');

    if (!this.video) return;

    this.isVisible = false;
    this.hasLoadedSources = false;
    this.prepareVideo();

    this.handleCanPlay = () => this.playWhenVisible();
    this.handlePlaying = () => this.classList.add('is-playing');
    this.handleVisibilityChange = () => {
      if (document.hidden) {
        this.video.pause();
      } else {
        this.playWhenVisible();
      }
    };
    this.handlePageShow = () => this.playWhenVisible();
    this.handleFirstInteraction = () => this.playWhenVisible();

    this.video.addEventListener('loadedmetadata', this.handleCanPlay);
    this.video.addEventListener('canplay', this.handleCanPlay);
    this.video.addEventListener('playing', this.handlePlaying);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('pageshow', this.handlePageShow);
    document.addEventListener('pointerdown', this.handleFirstInteraction, { passive: true, once: true });
    document.addEventListener('touchstart', this.handleFirstInteraction, { passive: true, once: true });

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = entry.isIntersecting;

        if (this.isVisible) {
          this.loadSources();
          this.playVideo();
        } else {
          this.video.pause();
        }
      },
      { rootMargin: '400px 0px' }
    );

    this.observer.observe(this);
    this.playWhenVisible();
  }

  disconnectedCallback() {
    if (!this.video) return;

    this.observer?.disconnect();
    this.video.removeEventListener('loadedmetadata', this.handleCanPlay);
    this.video.removeEventListener('canplay', this.handleCanPlay);
    this.video.removeEventListener('playing', this.handlePlaying);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('pageshow', this.handlePageShow);
    document.removeEventListener('pointerdown', this.handleFirstInteraction);
    document.removeEventListener('touchstart', this.handleFirstInteraction);
  }

  prepareVideo() {
    this.video.defaultMuted = true;
    this.video.muted = true;
    this.video.autoplay = true;
    this.video.loop = true;
    this.video.playsInline = true;
    this.video.controls = false;
    this.video.disablePictureInPicture = true;
    this.video.setAttribute('muted', '');
    this.video.setAttribute('playsinline', '');
    this.video.setAttribute('webkit-playsinline', '');
    this.video.removeAttribute('controls');
  }

  playWhenVisible() {
    if (this.isVisible && !document.hidden) {
      this.loadSources();
      this.playVideo();
    }
  }

  loadSources() {
    if (this.hasLoadedSources) return;

    const sources = this.video.querySelectorAll('source[data-src]');
    sources.forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });

    this.hasLoadedSources = true;
    this.video.load();
  }

  async playVideo() {
    if (!this.hasLoadedSources) return;

    this.prepareVideo();

    try {
      await this.video.play();
    } catch (_error) {
      // The poster remains visible if a browser or data-saving mode blocks autoplay.
    }
  }
}

if (!customElements.get('autoplay-inline-video')) {
  customElements.define('autoplay-inline-video', AutoplayInlineVideo);
}
